import { useLoaderData } from '@remix-run/react';
import React, { useMemo } from 'react';
import DebugComponent from '~/components/debug.tsx';
import UiComponent from '~/components/ui-component.tsx';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import { twMerge } from 'tailwind-merge';
import type { Feed, ScreenType } from '~/services/layout/index.ts';
import { SearchV2 } from '~/plugins/search_v2/components';
import { TabsScreen } from '~/plugins/quick-brick-tabs/components';
import { useScrollRestorationOnLazyLoading } from '~/hooks/use-scroll-restoration';
import { useGetStorageFeed } from '~/hooks/use-feed';
import {
  useIsLoggedIn,
  type UseIsLoggedInReturnType,
} from '~/hooks/use-is-logged-in';
import { SearchProvider, useSearchContext } from '~/contexts/SearchContext.tsx';

export default function GeneralContent(): JSX.Element {
  const {
    debug,
    serverLoadedFeeds,
    uiComponents,
    isSearchScreen,
    screen,
    isThemeV2,
    isContinueWatchingSyncedFeed,
    isFavoritesSyncedFeed,
  } = useLoaderData<typeof rootLoader>();

  if (isSearchScreen) {
    return (
      <SearchProvider>
        <SearchScreenContent />
      </SearchProvider>
    );
  }

  return (
    <ContentLayout
      screen={screen}
      isThemeV2={isThemeV2}
      isSearchScreenV2={false}
      searchScreens={undefined}
      searchScreenV2BackgroundColor={undefined}
      debug={debug}
      uiComponents={uiComponents}
      serverLoadedFeeds={serverLoadedFeeds}
      isContinueWatchingSyncedFeed={isContinueWatchingSyncedFeed}
      isFavoritesSyncedFeed={isFavoritesSyncedFeed}
    />
  );
}

/**
 * Content component for search screens - uses SearchContext for client-side search
 */
function SearchScreenContent(): JSX.Element {
  const {
    debug,
    serverLoadedFeeds,
    uiComponents,
    searchScreens,
    screen,
    isThemeV2,
    isContinueWatchingSyncedFeed,
    isFavoritesSyncedFeed,
  } = useLoaderData<typeof rootLoader>();

  const { fetcherData } = useSearchContext();

  const activeFeedData = fetcherData?.serverLoadedFeeds ?? serverLoadedFeeds;
  const activeUiComponents = fetcherData?.uiComponents ?? uiComponents;

  const searchScreenType: ScreenType | undefined = searchScreens?.[0]?.type;
  const isSearchScreenV2: boolean = searchScreenType === 'search_v2';

  const searchScreenV2BackgroundColor: string | undefined = isSearchScreenV2
    ? searchScreens?.[0].styles?.screen_background_color
    : undefined;

  return (
    <ContentLayout
      screen={screen}
      isThemeV2={isThemeV2}
      isSearchScreenV2={isSearchScreenV2}
      searchScreens={searchScreens}
      searchScreenV2BackgroundColor={searchScreenV2BackgroundColor}
      debug={debug}
      uiComponents={activeUiComponents}
      serverLoadedFeeds={activeFeedData}
      isContinueWatchingSyncedFeed={isContinueWatchingSyncedFeed}
      isFavoritesSyncedFeed={isFavoritesSyncedFeed}
    >
      {isSearchScreenV2 && <SearchV2 />}
    </ContentLayout>
  );
}

type ContentLayoutProps = {
  screen: any;
  isThemeV2: boolean;
  isSearchScreenV2: boolean;
  searchScreens: any;
  searchScreenV2BackgroundColor: string | undefined;
  debug: string | null | undefined;
  uiComponents: any[];
  serverLoadedFeeds: any[];
  isContinueWatchingSyncedFeed: boolean;
  isFavoritesSyncedFeed: boolean;
  children?: React.ReactNode;
};

/**
 * Shared layout component for rendering screen content
 */
function ContentLayout({
  screen,
  isThemeV2,
  isSearchScreenV2,
  searchScreens,
  searchScreenV2BackgroundColor,
  debug,
  uiComponents,
  serverLoadedFeeds,
  isContinueWatchingSyncedFeed,
  isFavoritesSyncedFeed,
  children,
}: ContentLayoutProps): JSX.Element {
  useScrollRestorationOnLazyLoading();

  const isLoggedInData: UseIsLoggedInReturnType = useIsLoggedIn();
  const isLoggedIn = isLoggedInData.isLoggedIn;

  const {
    feed: continueWatchingFeed,
    isUpdating: isContinueWatchingUpdating,
    isLoading: isContinueWatchingLoading,
  } = useGetStorageFeed({
    storageKey: 'continue-watching',
    condition: isContinueWatchingSyncedFeed,
    isLoggedIn,
  });

  const {
    feed: favoritesFeed,
    isUpdating: isFavoritesUpdating,
    isLoading: isFavoritesLoading,
  } = useGetStorageFeed({
    storageKey: 'favorites',
    condition: isFavoritesSyncedFeed,
    isLoggedIn,
  });

  const isTabsScreen: boolean = screen.type === 'quick-brick-tabs';

  const uiComponentProps = useMemo(
    () => ({
      isLoggedInData,
      storageFeedsData: {
        'continue-watching': {
          feed: continueWatchingFeed,
          isUpdating: isContinueWatchingUpdating,
          isLoading: isContinueWatchingLoading,
        },
        favorites: {
          feed: favoritesFeed,
          isUpdating: isFavoritesUpdating,
          isLoading: isFavoritesLoading,
        },
      },
    }),
    [
      isLoggedInData,
      continueWatchingFeed,
      isContinueWatchingUpdating,
      isContinueWatchingLoading,
      favoritesFeed,
      isFavoritesUpdating,
      isFavoritesLoading,
    ]
  );

  return (
    <div
      className={twMerge(
        `screen-${screen.id}`,
        'z-0',
        isThemeV2
          ? 'screen-classes'
          : 'mb-screen-b mt-screen-t min-h-[50vh] bg-screen pb-mobile-screen-b pl-mobile-screen-l pr-mobile-screen-r pt-mobile-screen-t lg:pb-screen-b lg:pl-screen-l lg:pr-screen-r lg:pt-screen-t',

        isSearchScreenV2 &&
          `search-v2-${searchScreens?.[0].id} search-screen-padding`
      )}
      style={{
        backgroundColor: searchScreenV2BackgroundColor
          ? searchScreenV2BackgroundColor
          : undefined,
      }}
    >
      {children}

      {React.Children.toArray(
        uiComponents.map((uiComponent: any) => {
          const feedData = serverLoadedFeeds.find(
            (feed: any) => feed.componentId === uiComponent.id
          );

          const error: string | undefined = feedData?.error;
          const feed = feedData?.feed as Feed | null | undefined;

          return (
            <div className="truncate">
              {debug === 'on' && (
                <DebugComponent
                  uiComponent={uiComponent}
                  feed={feed}
                  error={error}
                />
              )}

              <UiComponent
                uiComponent={uiComponent}
                feed={feed}
                {...uiComponentProps}
              />
            </div>
          );
        })
      )}

      {isTabsScreen && <TabsScreen {...uiComponentProps} />}
    </div>
  );
}
